import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, BookOpen, Phone, Loader2, AlertCircle } from 'lucide-react';


// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionId] = useState(searchParams.get('session_id'));
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [statusMessage, setStatusMessage] = useState('Verifying your payment...');
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 5;

  useEffect(() => {
    // Clear any pending payment state
    localStorage.removeItem('pendingPayment');
    
    // If we have a session_id, poll for payment status
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= maxPollAttempts) {
      setPaymentStatus('timeout');
      setStatusMessage('Payment verification is taking longer than expected. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_HOST}/api/stripe/checkout-status/${sid}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setStatusMessage('Payment successful! Your enrollment is confirmed.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setStatusMessage('Payment session expired. Please try again.');
        return;
      } else if (data.status === 'complete') {
        setPaymentStatus('success');
        setStatusMessage('Payment successful! Your enrollment is confirmed.');
        return;
      }

      // If still pending, continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Continue polling even on error
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    }
  };

  const renderStatusContent = () => {
    if (paymentStatus === 'checking') {
      return (
        <>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#3b82f6', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: 'pulse 2s infinite'
          }}>
            <Loader2 size={48} style={{ color: 'white', animation: 'spin 1s linear infinite' }} />
          </div>
          <h1 style={{ color: '#3b82f6', marginBottom: '16px', fontSize: '1.5rem' }}>
            Verifying Payment...
          </h1>
          <p style={{ color: '#64748b', fontSize: '1rem' }}>
            Please wait while we confirm your payment.
          </p>
        </>
      );
    }

    if (paymentStatus === 'success') {
      return (
        <>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#22c55e', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={48} style={{ color: 'white' }} />
          </div>
          
          <h1 style={{ color: '#166534', marginBottom: '16px', fontSize: '2rem' }}>
            Payment Successful!
          </h1>
          
          <p style={{ color: '#64748b', fontSize: '1.1rem', marginBottom: '32px', lineHeight: '1.6' }}>
            Thank you for your payment. Your enrollment has been confirmed and you now have full access to your training program.
          </p>
          
          {sessionId && (
            <div style={{ 
              background: '#f8fafc', 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '32px',
              fontSize: '0.875rem',
              color: '#64748b'
            }}>
              <strong>Transaction ID:</strong> {sessionId.substring(0, 24)}...
            </div>
          )}
          
          <div style={{ 
            background: '#eff6ff', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <h3 style={{ color: '#1e40af', marginBottom: '12px', fontSize: '1rem' }}>What's Next?</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#3b82f6', fontSize: '0.875rem' }}>
              <li style={{ marginBottom: '8px' }}>Check your email for a payment receipt and enrollment confirmation</li>
              <li style={{ marginBottom: '8px' }}>Access your training modules from the Student Dashboard</li>
              <li style={{ marginBottom: '8px' }}>Review the program schedule and start your training</li>
              <li>Contact us if you have any questions</li>
            </ul>
          </div>
        </>
      );
    }

    if (paymentStatus === 'expired' || paymentStatus === 'timeout') {
      return (
        <>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#f59e0b', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <AlertCircle size={48} style={{ color: 'white' }} />
          </div>
          
          <h1 style={{ color: '#b45309', marginBottom: '16px', fontSize: '1.5rem' }}>
            {paymentStatus === 'expired' ? 'Session Expired' : 'Verification Pending'}
          </h1>
          
          <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '24px' }}>
            {statusMessage}
          </p>
        </>
      );
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: paymentStatus === 'success' 
        ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
      <div className="card" style={{ 
        maxWidth: '600px', 
        width: '100%', 
        padding: '48px', 
        textAlign: 'center',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
      }}>
        {renderStatusContent()}
        
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="go-to-dashboard-btn"
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px', 
              background: '#1e40af', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              fontSize: '1rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <BookOpen size={20} />
            Go to Dashboard
            <ArrowRight size={20} />
          </button>
          
          <button
            onClick={() => navigate('/')}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px', 
              background: '#f1f5f9', 
              color: '#64748b', 
              border: 'none', 
              borderRadius: '8px', 
              fontSize: '1rem', 
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Return Home
          </button>
        </div>
        
        <div style={{ 
          marginTop: '32px', 
          paddingTop: '24px', 
          borderTop: '1px solid #e5e7eb',
          color: '#64748b',
          fontSize: '0.875rem'
        }}>
          <p style={{ marginBottom: '8px' }}>
            <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Questions? Call us at <strong>901.625.1201</strong>
          </p>
          <p style={{ margin: 0 }}>
            MedCare Nursing Institute & Training Academy Inc.
          </p>
        </div>
      </div>
    </div>
  );
}
